import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import { useAlertStore } from './alert';
import { useEmployeesStore } from './employees';
import { useSiteStore } from './site';
import app from '@/main.js';

export const useAdminStore = defineStore('admin', {
    state: () => ({
        companyId: null,
        /** Company hash last applied by admin-company routing guard (distinct from user's company hash). */
        workflowCompanyHash: null,
        sectorId: null,
        allSectors: [],
        activeSectors: [],

        selectedSectorId: null,

        fetching: false,
        fetchPromise: null,
    }),
    actions: {
        setCompanyId(companyId) {
            this.companyId = companyId;
        },
        /**
         * User-visible toast + full workflow reset so we never keep a previous
         * company_id while the URL targets a different (or failed) company hash.
         */
        handleFailedCompanyWorkflowEnsure(message) {
            const alertStore = useAlertStore();
            alertStore.pushError(
                message || 'Could not load admin company context.'
            );
            this.clearCompanyWorkflow();
        },
        /** Resolve admin company workflow by URL hash (Company Detail · AdminAuditOverview). */
        async ensureCompanyWorkflowFromHash(companyHash) {
            const trimmed = String(companyHash || '').trim();
            if (!trimmed || !app) return;

            if (
                this.workflowCompanyHash === trimmed &&
                Number.isInteger(Number(this.companyId)) &&
                Number(this.companyId) > 0
            ) {
                return;
            }

            const url =
                app.config.globalProperties.$env.apiRoot + 'company/read';
            const payload = {
                where: { hash: trimmed },
                fields: ['id'],
            };

            try {
                const response = await httpAPI(
                    url,
                    payload,
                    'GET',
                    app.config.globalProperties.$router
                );
                if (!response?.success || !response.data?.[0]?.id) {
                    this.handleFailedCompanyWorkflowEnsure(
                        response?.message ||
                            'Company not found for this admin link.'
                    );
                    return;
                }

                const companyId = Number(response.data[0].id);
                if (!Number.isInteger(companyId) || companyId <= 0) {
                    this.handleFailedCompanyWorkflowEnsure(
                        'Invalid company record for this admin link.'
                    );
                    return;
                }

                this.setCompanyId(companyId);
                this.workflowCompanyHash = trimmed;

                const employeesStore = useEmployeesStore();
                employeesStore.clear();
                employeesStore.fetchEmployees();
                employeesStore.fetchCompanyOwner(companyId);

                const siteStore = useSiteStore();
                siteStore.clear();
                siteStore.fetchSites();
            } catch (error) {
                console.error('ensureCompanyWorkflowFromHash failed:', error);
                this.handleFailedCompanyWorkflowEnsure(
                    error?.message ||
                        'Failed to load company for admin navigation.'
                );
            }
        },
        /** Restore default user-company scoped employees/sites after leaving admin company routes. */
        clearCompanyWorkflow() {
            this.setCompanyId(null);
            this.workflowCompanyHash = null;

            const employeesStore = useEmployeesStore();
            employeesStore.clear();
            employeesStore.fetchEmployees();

            const siteStore = useSiteStore();
            siteStore.clear({ preserveGlobalSiteSelection: true });
            siteStore.initializeSiteStore();
        },
        setSelectedSectorId(sectorId) {
            this.selectedSectorId = sectorId;
        },
        fetchSectors() {
            if (this.fetching && this.fetchPromise) {
                return this.fetchPromise;
            }
            this.fetching = true;
            const url =
                app.config.globalProperties.$env.apiRoot +
                'company/sector/read';
            const vm = this;
            this.fetchPromise = httpAPI(
                url,
                null,
                'GET',
                app.config.globalProperties.$router
            )
                .then((response) => {
                    if (response.success) {
                        vm.allSectors = response.data || [];
                        vm.activeSectors = vm.allSectors.filter(
                            (sector) => !sector?.archived
                        );
                    } else {
                        const alertStore = useAlertStore();
                        alertStore.pushError(
                            'Failed to fetch sectors: ' + response?.message
                        );
                    }
                    return vm.allSectors;
                })
                .finally(() => {
                    vm.fetching = false;
                    vm.fetchPromise = null;
                });
            return this.fetchPromise;
        },
        syncSectors() {
            if (this.allSectors && this.allSectors.length > 0) {
                return Promise.resolve(this.allSectors);
            }
            return this.fetchSectors();
        },
    },
    getters: {
        getSectorsOptions(state) {
            return state.allSectors.map((sector) => ({
                text: sector.name + (sector?.archived ? ' (Archived)' : ''),
                value: sector.id,
            }));
        },
    },
});
